// src/components/admin/AdminJobs.jsx
import { useState, useEffect } from 'react';
import {
  collection, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, query, orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';

const EMPTY_FORM = {
  title: '', department: '', type: 'Full time', level: 'Mid level',
  deadline: '', description: '', requirements: '', tags: '',
};

export default function AdminJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  async function fetchJobs() {
    setLoading(true);
    try {
      const q = query(collection(db, 'jobs'), orderBy('title'));
      const snap = await getDocs(q);
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchJobs(); }, []);

  function openCreate() {
    setEditingJob(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setMsg(null);
  }

  function openEdit(job) {
    setEditingJob(job);
    setForm({
      title: job.title || '',
      department: job.department || '',
      type: job.type || job.period || 'Full time',
      level: job.level || 'Mid level',
      deadline: job.deadline || '',
      description: job.description || job.desc || '',
      requirements: job.requirements || '',
      tags: typeof job.tags === 'string' ? job.tags : (job.tags || []).join(', '),
    });
    setShowForm(true);
    setMsg(null);
  }

  function closeForm() { setShowForm(false); setEditingJob(null); setMsg(null); }

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const data = { ...form, updatedAt: serverTimestamp() };
      if (editingJob) {
        await updateDoc(doc(db, 'jobs', editingJob.id), data);
        setMsg({ type: 'success', text: 'Job updated.' });
      } else {
        await addDoc(collection(db, 'jobs'), { ...data, createdAt: serverTimestamp() });
        setMsg({ type: 'success', text: 'Job created.' });
      }
      await fetchJobs();
      closeForm();
    } catch (err) {
      setMsg({ type: 'error', text: 'Error saving job. ' + err.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(job) {
    if (!window.confirm(`Delete "${job.title}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'jobs', job.id));
      await fetchJobs();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
        <h2 className="title-4">Job Postings</h2>
        <button className="btn btn-primary" onClick={openCreate}>+ New Job</button>
      </div>

      {msg && (
        <div style={{ padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', background: msg.type === 'success' ? '#dcfce7' : '#fee2e2', color: msg.type === 'success' ? '#16a34a' : '#dc2626', fontSize: '14px' }}>
          {msg.text}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="admin-form-panel">
          <h3 className="title-6" style={{ marginBottom: '24px' }}>{editingJob ? `Edit: ${editingJob.title}` : 'Create New Job'}</h3>
          <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Job Title *</label>
                <input name="title" className="form-input" value={form.title} onChange={handleChange} required placeholder="e.g. Software Engineer" />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input name="department" className="form-input" value={form.department} onChange={handleChange} placeholder="e.g. Engineering" />
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <div className="form-select-wrap">
                  <select name="type" className="form-select" value={form.type} onChange={handleChange}>
                    <option value="Full time">Full time</option>
                    <option value="Contract">Contract</option>
                    <option value="Part time">Part time</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Level</label>
                <div className="form-select-wrap">
                  <select name="level" className="form-select" value={form.level} onChange={handleChange}>
                    <option value="Junior level">Junior level</option>
                    <option value="Mid level">Mid level</option>
                    <option value="Senior level">Senior level</option>
                    <option value="Lead">Lead</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Deadline</label>
                <input name="deadline" className="form-input" value={form.deadline} onChange={handleChange} placeholder="e.g. 31st Dec, 2026" />
              </div>
              <div className="form-group">
                <label className="form-label">Tags (comma-separated)</label>
                <input name="tags" className="form-input" value={form.tags} onChange={handleChange} placeholder="React, Node.js, AWS" />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Job Description *</label>
              <textarea name="description" className="form-textarea" value={form.description} onChange={handleChange} required placeholder="Detailed job description..." style={{ height: '120px' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Requirements (one per line)</label>
              <textarea name="requirements" className="form-textarea" value={form.requirements} onChange={handleChange} placeholder="3+ years of experience&#10;Strong communication skills" style={{ height: '120px' }} />
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingJob ? 'Update Job' : 'Create Job'}
              </button>
              <button type="button" className="btn btn-outline" onClick={closeForm}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Jobs Table */}
      {loading ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--token-desc)' }}>Loading...</div>
      ) : jobs.length === 0 ? (
        <div style={{ padding: '48px', textAlign: 'center', color: 'var(--token-desc)', background: '#fff', borderRadius: '8px' }}>
          No jobs yet. Click "+ New Job" to create one.
        </div>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Department</th>
              <th>Type</th>
              <th>Level</th>
              <th>Deadline</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(job => (
              <tr key={job.id}>
                <td><strong>{job.title}</strong></td>
                <td>{job.department || '—'}</td>
                <td>{job.type || job.period || '—'}</td>
                <td>{job.level || '—'}</td>
                <td>{job.deadline || '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="btn btn-outline" style={{ padding: '4px 12px', height: 'auto', fontSize: '13px' }} onClick={() => openEdit(job)}>Edit</button>
                    <button style={{ padding: '4px 12px', height: 'auto', fontSize: '13px', background: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '6px', cursor: 'pointer' }} onClick={() => handleDelete(job)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}